
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from "@/lib/hooks/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Menu, X, User } from "lucide-react";
import { LanguageSelector } from "./LanguageSelector";
import { toast } from "sonner";

export const Navbar: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const location = useLocation();
  const { user, signOut, isAdmin, loading } = useAuth();
  
  // Debug auth state on mount and when it changes
  React.useEffect(() => {
    console.log("[Navbar] Auth state updated:", { 
      user: user?.email || "null", 
      userId: user?.id || "null", 
      isAdmin,
      authenticated: !!user,
      loading
    });
  }, [user, isAdmin, loading]);

  // Function to get user initials for avatar
  const getUserInitials = (): string => {
    if (user?.firstName && user.firstName.length > 0) {
      return user.firstName.charAt(0).toUpperCase();
    }
    if (user?.email && user.email.length > 0) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U"; // Default fallback
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container max-w-7xl flex h-16 items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center">
            <span className="font-display font-medium text-2xl text-foreground">Fatato</span>
            <span className="text-primary ml-1">Studio</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className={`text-sm font-medium ${location.pathname === "/" ? "text-foreground" : "text-muted-foreground"} hover:text-foreground transition-colors`}>
              {t("nav.home")}
            </Link>
            <Link to="/classes" className={`text-sm font-medium ${location.pathname === "/classes" ? "text-foreground" : "text-muted-foreground"} hover:text-foreground transition-colors`}>
              {t("nav.classes")}
            </Link>
            <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {t("nav.about")}
            </a>
            <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
              {t("nav.contact")}
            </a>
          </nav>
        </div>
        
        <div className="flex items-center gap-4">
          <LanguageSelector />
          
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-muted animate-pulse"></div>
            </div>
          ) : isAuthenticated ? (
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm">
                    {t("nav.admin")}
                  </Button>
                </Link>
              )}
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  {t("nav.dashboard")}
                </Button>
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-full h-8 w-8 p-0">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/10 text-primary font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm mb-1">
                    {t("nav.loggedInAs")}: <span className="font-semibold">{user?.email}</span>
                    {isAdmin && <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{t("nav.admin")}</span>}
                  </div>
                  <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer">
                    {t("nav.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login">
                <Button variant="ghost" size="sm">
                  {t("nav.login")}
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="default" size="sm">
                  {t("nav.register")}
                </Button>
              </Link>
            </div>
          )}
          
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right">
              <div className="flex flex-col gap-6 pt-6">
                <nav className="flex flex-col gap-4">
                  <Link to="/" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium">
                    {t("nav.home")}
                  </Link>
                  <Link to="/classes" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium">
                    {t("nav.classes")}
                  </Link>
                  <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium">
                    {t("nav.about")}
                  </a>
                  <a href="https://fatato.am/" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium">
                    {t("nav.contact")}
                  </a>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setIsMenuOpen(false)} className="text-lg font-medium text-primary">
                      {t("nav.admin")}
                    </Link>
                  )}
                </nav>
                
                {isAuthenticated ? (
                  <div className="flex flex-col gap-4 mt-4">
                    <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full">
                        {t("nav.dashboard")}
                      </Button>
                    </Link>
                    <Button variant="outline" onClick={() => {
                      signOut();
                      setIsMenuOpen(false);
                    }}>
                      {t("nav.logout")}
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 mt-4">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="outline" className="w-full">
                        {t("nav.login")}
                      </Button>
                    </Link>
                    <Link to="/register" onClick={() => setIsMenuOpen(false)}>
                      <Button className="w-full">
                        {t("nav.register")}
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
